import React, { FC, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { FeatureFlag } from 'app/api/services/site-configs/types';
import { ReduxState } from 'app/redux/types';

import { Button } from 'app/design-system/components/Button';
import { Spinner } from 'app/design-system/components/Spinner';
import { ButtonIntent } from 'app/design-system/types/Button';
import { useTranslation } from 'app/lang';
import { selectClientInfoLoading } from 'app/lib/account/selectors';
import { useFeatureFlag } from 'app/lib/feature-flags';
import { registration as selectRegistration } from 'app/lib/site-configs/selectors';
import { openRegistrationDialog } from 'app/modules/Signing/actions';
import { createSigningQuery } from 'app/modules/Signing/utils/signing-modals-query';
import { createAuthQuery } from 'app/modules/SigningNew/utils/auth-modals-query';

import EmailCollector from '../EmailCollector/EmailCollector';

import dictionary from './AuthBlock.dictionary.json';

import styles from './AuthBlock.module.scss';

type AuthBlockProps = {
  clientInfoLoading: boolean;
  isRegistrationDisabled?: boolean;
};

const AuthBlock: FC<AuthBlockProps> = ({
  clientInfoLoading,
  isRegistrationDisabled,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAllowReg = useFeatureFlag(FeatureFlag.ALLOW_REGISTRATION);
  const isAuthentication = useFeatureFlag(FeatureFlag.AUTHENTICATION);

  const handleSignupClick = useCallback(() => {
    dispatch(openRegistrationDialog());
  }, []);

  if (clientInfoLoading) {
    return (
      <div className={styles.spinnerPosition}>
        <Spinner size={30} />
      </div>
    );
  }

  return (
    <>
      <ul data-test="authBlock-container"
          className={styles.signList}>
        {isAuthentication ?(
          <li>
            <Button component={Link}
                    to={createAuthQuery('sign')}
                    data-test="authBlock-loginBtn"
                    data-ga-event="header-login"
                    intent={ButtonIntent.CALL_TO_ACTION}
                    className={styles.signing}>
              {t(dictionary.buttonLogin)}
            </Button>
          </li>
        ):(
          <>
            <li>
              <Link to={createSigningQuery('login')}
                    data-test="authBlock-loginBtn"
                    data-ga-event="header-login"
                    className={styles.signIn}>
                {t(dictionary.buttonLogin)}
              </Link>
            </li>
            {isAllowReg && (
              <li>
                <Button data-test="authBlock-registrationBtn"
                        data-ga-event="header-registration"
                        intent={ButtonIntent.CALL_TO_ACTION}
                        className={styles.registrationBtn}
                        onClick={handleSignupClick}
                        disabled={isRegistrationDisabled}>
                  {t(dictionary.buttonRegistration)}
                </Button>
              </li>
            )}
          </>
        )}
      </ul>
      <EmailCollector />
    </>
  );
};

const mapStateToProps = (state: ReduxState): AuthBlockProps => ({
  clientInfoLoading: selectClientInfoLoading(state),
  isRegistrationDisabled: selectRegistration(state)?.disabled,
});

export default connect(mapStateToProps)(AuthBlock);
